import type { Device, DeviceMaster } from '../../domain/entities/Device';
import type { EnrollDeviceParams } from '../../domain/value-objects/EnrollDeviceParams';
import type { DeviceDTO, DeviceMasterDTO } from '../dto/DeviceDTO';
import type { EnrollDeviceRequestDTO } from '../dto/EnrollDeviceRequestDTO';
import { normalizePgTimezone } from '@/shared/utils/timezone';

/**
 * PostgreSQL does not accept IANA timezones with the 'America/Argentina/' subpath.
 * This map covers the timezones that Intl.DateTimeFormat can return for Argentina.
 * Any unrecognized timezone passes through unchanged.
 */
// const PG_TIMEZONE_MAP: Record<string, string> = {
//   'America/Argentina/Buenos_Aires':  'America/Buenos_Aires',
//   'America/Argentina/Cordoba':       'America/Cordoba',
//   'America/Argentina/Salta':         'America/Salta',
//   'America/Argentina/Jujuy':         'America/Jujuy',
//   'America/Argentina/Tucuman':       'America/Tucuman',
//   'America/Argentina/Catamarca':     'America/Catamarca',
//   'America/Argentina/La_Rioja':      'America/La_Rioja',
//   'America/Argentina/San_Juan':      'America/San_Juan',
//   'America/Argentina/Mendoza':       'America/Mendoza',
//   'America/Argentina/San_Luis':      'America/Argentina/San_Luis',
//   'America/Argentina/Rio_Gallegos':  'America/Argentina/Rio_Gallegos',
//   'America/Argentina/Ushuaia':       'America/Argentina/Ushuaia',
// };

// function normalizePgTimezone(tz: string): string {
//   return PG_TIMEZONE_MAP[tz] ?? tz;
// }

export function toDevice(dto: DeviceDTO): Device {
  return {
    idDevice:     dto.id_device,
    // Backend sends device_name on the clock/app endpoint; name_device is a legacy alias
    name:         dto.device_name ?? dto.name_device ?? '',
    serialNumber: dto.serial_number ?? dto.settings_device?.serial ?? null,
    model:        dto.model ?? null,
    isActive:     dto.status === 1,
    idEntity:     dto.id_entity ?? 0,
    enrolledAt:   dto.at_create ?? null,
  };
}

export function toDeviceMaster(dto: DeviceMasterDTO): DeviceMaster {
  return {
    idDevice: dto.id_device,
    serialNumber: dto.serial_number,
    model: dto.model,
    brand: dto.brand,
  };
}

/**
 * Converts domain EnrollDeviceParams → EnrollDeviceRequestDTO (API boundary).
 * This is the ONLY place in the codebase that knows the backend's field names
 * and casing for the enroll endpoint (IdUnique, timezone_pg, etc.).
 * The domain type uses pure camelCase and has no knowledge of the API contract.
 *
 * NOTE: PostgreSQL rejects IANA timezones with the 'America/Argentina/' subpath.
 * The normalization below maps known offenders to their PG-compatible equivalent.
 * Confirmed via API testing: 'America/Argentina/Buenos_Aires' → if118 (400),
 * 'America/Buenos_Aires' → 204 (accepted).
 */
export function toEnrollDeviceRequestDTO(params: EnrollDeviceParams): EnrollDeviceRequestDTO {
  const dto: EnrollDeviceRequestDTO = {
    IdUnique:    params.idUnique,
    modelName:   params.modelName,
    serial:      params.serial,
    timezone_pg: normalizePgTimezone(params.timezone),
  };
  if (params.idDevice !== undefined) {
    dto.id_device = params.idDevice;
  }
  return dto;
}
